import { format, isValid, parseISO } from 'date-fns';
import { it } from 'date-fns/locale';

// Rileva il locale del browser
const getBrowserLocale = () => {
  const browserLocale = navigator.language || navigator.userLanguage || 'it-IT';
  return browserLocale.split('-')[0];
};

// Formattatore di date usando Intl
const dateFormatter = new Intl.DateTimeFormat(getBrowserLocale(), {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric'
});

/**
 * Converte vari formati di data in un oggetto Date
 * @param {Date|Object|string|null} date - Data da convertire
 * @returns {Date|null} Data convertita o null se invalida
 */
export const parseDate = (date) => {
  if (!date) return null;
  
  try {
    if (date instanceof Date) {
      return isValid(date) ? date : null;
    }
    
    if (date.seconds) { // Timestamp Firestore
      return new Date(date.seconds * 1000);
    }
    
    if (typeof date === 'string') {
      // Prova prima a parsare come data ISO
      const isoDate = parseISO(date);
      if (isValid(isoDate)) return isoDate;
      
      // Se non è una data ISO, prova a parsare come data locale
      const parts = date.split(/[/.-]/);
      if (parts.length === 3) {
        // Assume DD/MM/YYYY se il primo numero è <= 31
        const isEuropeanFormat = parseInt(parts[0]) <= 31;
        const [day, month, year] = isEuropeanFormat ? parts : [parts[1], parts[0], parts[2]];
        const parsedDate = new Date(year, month - 1, day);
        if (isValid(parsedDate)) return parsedDate;
      }
    }
    
    return null;
  } catch (error) {
    console.error('Error parsing date:', error);
    return null;
  }
};

/**
 * Formatta una data nel formato locale
 * @param {Date|Object|string|null} date - Data da formattare
 * @param {string} [formatString] - Formato personalizzato per date-fns
 * @param {string} [defaultValue='-'] - Valore da restituire se la data è invalida
 * @returns {string} Data formattata o defaultValue
 */
export const formatDate = (date, formatString, defaultValue = '-') => {
  const parsedDate = parseDate(date);
  if (!parsedDate) return defaultValue;
  
  try {
    if (formatString) {
      // Se è fornito un formato personalizzato, usa date-fns direttamente
      return format(parsedDate, formatString, { locale: it });
    }
    
    // Altrimenti usa il formatter di Intl
    return dateFormatter.format(parsedDate);
  } catch (error) {
    console.error('Error formatting date:', error);
    // Fallback a date-fns con locale italiano
    try {
      return format(parsedDate, 'dd/MM/yyyy', { locale: it });
    } catch {
      return defaultValue;
    }
  }
};

/**
 * Prepara una data per Firestore
 * @param {Date|null} date - Data da preparare
 * @returns {Date|null} Data pronta per essere salvata
 */
export const prepareDateForFirestore = (date) => {
  const parsedDate = parseDate(date);
  return parsedDate;
};

/**
 * Ottiene il locale corrente per i componenti DatePicker
 * @returns {Object} Locale object per react-datepicker
 */
export const getDatePickerLocale = () => {
  // Per ora ritorniamo sempre il locale italiano per consistenza
  // Ma potremmo implementare altri locali in futuro
  return it;
}; 